import referralApi from '@/api/referral'
import {
  loadReditusScript, initReditusWidget, showReditusWidget, isMockMode,
} from '@/services/loaderPlugin/reditus.loader'
import * as Sentry from '@sentry/vue'

// Delays (ms) to re-fetch reward status after the widget opens. The Reditus
// SDK exposes no lifecycle events for "user joined the program", so we poll
// our own BE — each call triggers a server-side active pull of the advocate
// data from Reditus, which caches the slug on User.reditusSlug once Reditus
// has created the advocate. Covers the window between clicking "Join
// referral program" in the modal and the user navigating away.
const POST_OPEN_REFRESH_DELAYS_MS = [2000, 5000, 15000, 60000]

const state = {
  widgetLoaded: false,
  hasUnreadReward: false,
  mockMode: false,
  showMockModal: false,
  postOpenRefreshTimers: [],
}

const _getters = {
  isWidgetReady: (s) => s.widgetLoaded,
  hasUnreadReward: (s) => s.hasUnreadReward,
  isMockMode: (s) => s.mockMode,
  showMockModal: (s) => s.showMockModal,
}

/* eslint-disable no-param-reassign */
const _mutations = {
  SET_WIDGET_LOADED(s, val) { s.widgetLoaded = val },
  SET_HAS_UNREAD_REWARD(s, val) { s.hasUnreadReward = val },
  SET_MOCK_MODE(s, val) { s.mockMode = val },
  SET_SHOW_MOCK_MODAL(s, val) { s.showMockModal = val },
  SET_POST_OPEN_REFRESH_TIMERS(s, val) { s.postOpenRefreshTimers = val },
}
/* eslint-enable no-param-reassign */

const _actions = {
  async initWidget({ commit, rootState }) {
    try {
      referralApi.initResource()
      const tokenData = await referralApi.getToken()

      if (tokenData.mock) {
        commit('SET_MOCK_MODE', true)
        commit('SET_WIDGET_LOADED', true)
        commit('SET_HAS_UNREAD_REWARD', true)
        return
      }

      await loadReditusScript()

      const user = rootState.currentUser
      initReditusWidget(tokenData.productId, tokenData.token, {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
      })

      commit('SET_MOCK_MODE', isMockMode())
      commit('SET_WIDGET_LOADED', true)
    } catch (err) {
      // API unavailable or token fetch failed — fall back to mock mode
      // so the UI still works (shows mock modal on click)
      console.warn('Referral widget init failed, falling back to mock mode:', err.message)
      Sentry.captureException(err)
      commit('SET_MOCK_MODE', true)
      commit('SET_WIDGET_LOADED', true)
    }
  },

  async fetchRewardStatus({ commit, state: s }) {
    if (s.mockMode) {
      commit('SET_HAS_UNREAD_REWARD', true)
      return
    }
    try {
      const data = await referralApi.getRewardStatus()
      commit('SET_HAS_UNREAD_REWARD', data.hasUnreadReward)
    } catch (err) {
      Sentry.captureException(err)
    }
  },

  async acknowledgeReward({ commit, state: s }) {
    commit('SET_HAS_UNREAD_REWARD', false)
    if (!s.mockMode) {
      try {
        await referralApi.acknowledgeReward()
      } catch (err) {
        Sentry.captureException(err)
      }
    }
  },

  openWidget({ commit, dispatch, state: s }) {
    if (s.mockMode) {
      commit('SET_SHOW_MOCK_MODAL', true)
    } else {
      showReditusWidget()
    }
    if (s.hasUnreadReward) {
      dispatch('acknowledgeReward')
    }

    // Schedule follow-up reward status refreshes. Each one triggers the BE
    // active pull so that once Reditus creates the advocate (after the user
    // clicks "Join referral program" inside the modal), we cache the slug
    // without waiting for the next full page load. Clear any previous
    // timers first so repeated widget opens don't stack duplicates.
    if (!s.mockMode) {
      dispatch('scheduleDelayedRefresh')
    }
  },

  scheduleDelayedRefresh({
    commit,
    dispatch,
    rootState,
    state: s,
  }) {
    s.postOpenRefreshTimers.forEach((timerId) => clearTimeout(timerId))
    const timers = POST_OPEN_REFRESH_DELAYS_MS.map((delay) => setTimeout(() => {
      // Belt-and-suspenders: skip the fetch if the user has logged out
      // since the timer was scheduled. The logout path also dispatches
      // cancelDelayedRefresh, but a race or a router.push-only logout
      // might leave timers pending.
      if (!rootState.currentUser || !rootState.currentUser.id) return
      dispatch('fetchRewardStatus')
    }, delay))
    commit('SET_POST_OPEN_REFRESH_TIMERS', timers)
  },

  cancelDelayedRefresh({ commit, state: s }) {
    s.postOpenRefreshTimers.forEach((timerId) => clearTimeout(timerId))
    commit('SET_POST_OPEN_REFRESH_TIMERS', [])
  },

  closeMockModal({ commit }) {
    commit('SET_SHOW_MOCK_MODAL', false)
  },
}

export default {
  namespaced: true,
  state,
  getters: _getters,
  mutations: _mutations,
  actions: _actions,
}
